import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/** GHG Protocol Scope 3 categories (mirrors backend list). */
const GHG_CATEGORIES = [
	{ id: 1, name: "1. Purchased goods & services" },
	{ id: 2, name: "2. Capital goods" },
	{ id: 3, name: "3. Fuel- and energy-related activities" },
	{ id: 4, name: "4. Upstream transportation & distribution" },
	{ id: 5, name: "5. Waste generated in operations" },
	{ id: 6, name: "6. Business travel" },
	{ id: 7, name: "7. Employee commuting" },
	{ id: 8, name: "8. Upstream leased assets" },
	{ id: 9, name: "9. Downstream transportation & distribution" },
	{ id: 10, name: "10. Processing of sold products" },
	{ id: 11, name: "11. Use of sold products" },
	{ id: 12, name: "12. End-of-life treatment of sold products" },
	{ id: 13, name: "13. Downstream leased assets" },
	{ id: 14, name: "14. Franchises" },
	{ id: 15, name: "15. Investments" },
];

interface Factor {
	factorID: number;
	sectorName: string;
	supplyChainPerAud: number;
}
interface SupplierFactor {
	factorID: number;
	name: string;
	sourceType: string;
}
interface Entry {
	spendID: number;
	description: string | null;
	amountAud: number;
	spendYear: number;
	sectorName: string;
	tonnesCo2e: number;
}
interface YearRow {
	year: number;
	totalSpendAud: number;
	tonnesCo2e: number;
}

/** Spend-based Scope 3: enter spend by category, get a CO2e estimate. */
const SpendBased: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [factors, setFactors] = useState<Factor[]>([]);
	const [supplierFactors, setSupplierFactors] = useState<SupplierFactor[]>([]);
	const [supplierFactor, setSupplierFactor] = useState<SupplierFactor | null>(null);
	const [entries, setEntries] = useState<Entry[]>([]);
	const [summary, setSummary] = useState<YearRow[]>([]);
	const [msg, setMsg] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [factor, setFactor] = useState<Factor | null>(null);
	const [amount, setAmount] = useState("");
	const [year, setYear] = useState(String(new Date().getFullYear() - 1));
	const [desc, setDesc] = useState("");
	const [ghgCat, setGhgCat] = useState(GHG_CATEGORIES[0]);

	const call = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			const data = await res.json().catch(() => ({}));
			return { ok: res.ok, data: data.Data || data };
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		const [f, e, cf] = await Promise.all([
			call("/api/spend-factors"),
			call("/api/spend-entries"),
			call("/api/custom-factors"),
		]);
		if (f.ok) setFactors(f.data.factors || []);
		if (cf.ok)
			setSupplierFactors(
				(cf.data.factors || []).filter(
					(x: SupplierFactor) => x.sourceType === "supplier-spend",
				),
			);
		if (e.ok) {
			setEntries(e.data.entries || []);
			setSummary(e.data.summary || []);
		}
	}, [call]);

	useEffect(() => {
		load();
	}, [load]);

	const save = async () => {
		setBusy(true);
		setMsg(null);
		try {
			const r = await call("/api/spend-entries", {
				method: "POST",
				body: JSON.stringify({
					factorId: factor?.factorID,
					amountAud: amount,
					spendYear: year,
					description: desc || undefined,
					ghgCategoryId: ghgCat?.id ?? 1,
					customFactorId: supplierFactor?.factorID,
				}),
			});
			setMsg(r.data.Message || r.data.message || null);
			if (r.ok) {
				setAmount("");
				setDesc("");
				await load();
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<Box maxWidth={950} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Spend-based Scope 3
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Estimate supply-chain emissions from what you spend: pick a
				category, enter the amount in AUD, and the platform applies an
				Australian emissions intensity for that industry (tonnes CO2e).
				Estimates use the XIO-SA dataset (CC BY-SA 4.0).
			</Typography>

			{msg && (
				<Alert severity="info" sx={{ my: 2 }}>
					{msg}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
					<Autocomplete
						options={factors}
						getOptionLabel={(o) => o.sectorName}
						value={factor}
						onChange={(_, v) => setFactor(v)}
						size="small"
						sx={{ minWidth: 320 }}
						renderInput={(p) => (
							<TextField {...p} label="Spend category" />
						)}
					/>
					<TextField
						label="Amount (AUD)"
						size="small"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						sx={{ width: 140 }}
					/>
					<TextField
						label="Year"
						size="small"
						value={year}
						onChange={(e) => setYear(e.target.value)}
						sx={{ width: 100 }}
					/>
					<Autocomplete
						options={GHG_CATEGORIES}
						getOptionLabel={(o) => o.name}
						value={ghgCat}
						onChange={(_, v) => setGhgCat(v || GHG_CATEGORIES[0])}
						size="small"
						sx={{ minWidth: 280 }}
						renderInput={(p) => (
							<TextField {...p} label="GHG category" />
						)}
					/>
					{supplierFactors.length > 0 && (
						<Autocomplete
							options={supplierFactors}
							getOptionLabel={(o) => o.name}
							value={supplierFactor}
							onChange={(_, v) => setSupplierFactor(v)}
							size="small"
							sx={{ minWidth: 240 }}
							renderInput={(p) => (
								<TextField
									{...p}
									label="Supplier factor (optional)"
								/>
							)}
						/>
					)}
					<TextField
						label="Description (optional)"
						size="small"
						value={desc}
						onChange={(e) => setDesc(e.target.value)}
						sx={{ minWidth: 200, flexGrow: 1 }}
					/>
					<Button
						variant="contained"
						color="secondary"
						disabled={busy || !factor || !amount}
						onClick={save}
					>
						Add spend
					</Button>
				</Box>
			</Paper>

			{summary.length > 0 && (
				<Paper
					elevation={0}
					sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
				>
					<Typography variant="subtitle1" gutterBottom>
						Estimated Scope 3 from spend
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Year</TableCell>
								<TableCell>Total spend (AUD)</TableCell>
								<TableCell>Estimate (t CO2e)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{summary.map((y) => (
								<TableRow key={y.year}>
									<TableCell>{y.year}</TableCell>
									<TableCell>{y.totalSpendAud}</TableCell>
									<TableCell>{y.tonnesCo2e}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				{entries.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No spend entries yet.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Year</TableCell>
								<TableCell>Category</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>AUD</TableCell>
								<TableCell>t CO2e</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{entries.map((e) => (
								<TableRow key={e.spendID}>
									<TableCell>{e.spendYear}</TableCell>
									<TableCell>{e.sectorName}</TableCell>
									<TableCell>
										{e.description || "—"}
									</TableCell>
									<TableCell>{e.amountAud}</TableCell>
									<TableCell>{e.tonnesCo2e}</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											aria-label="Remove entry"
											onClick={async () => {
												await call(
													`/api/spend-entries?spendId=${e.spendID}`,
													{ method: "DELETE" },
												);
												await load();
											}}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Box>
	);
};

export default SpendBased;
